/*
 * -----------------------------------------------------------------------
 *  <copyright file="rate/display.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import {
    HC_COLORS_STANDARD,
    HC_THRESHOLD_NAMES,
    IOpraChartPlot,
    IOpraChartSeries,
    OpraChartLineStyle,
    OpraChartSeriesType,
    OpraColours,
    OpraMarkerShape,
    OpraRag,
    PURPLE_NAMES,
} from 'interfaces/opra-chart.interface';
import { DashboardType, PanelSiteScoring } from 'types/graphql-global-types';
import ScatterLineChart from 'views/components/core/charts/scatter-line-chart';

const getRateGroup = ({ getRef, panelState, sites, siteId, setLoadingComplete }: any) => {
    if (!panelState?.panelData?.panelData?.length) {
        if (setLoadingComplete) setLoadingComplete();

        return <span>No data</span>;
    }

    const tooltipShowSeriesLabel = true;
    const tooltipShowSeriesThreshold = false;
    const series = [OpraRag.Green, OpraRag.Amber, OpraRag.Red];

    // Fix for 'HIGHERPURPLE' and 'LOWERPURPLE' in the config
    let modelSiteScoring = panelState.modelConfig.siteScoring;
    if (modelSiteScoring === PanelSiteScoring.HIGHER_PURPLE.replace('_', ''))
        modelSiteScoring = PanelSiteScoring.HIGHER_PURPLE;

    if (modelSiteScoring === PanelSiteScoring.LOWER_PURPLE.replace('_', ''))
        modelSiteScoring = PanelSiteScoring.LOWER_PURPLE;

    if (modelSiteScoring === PanelSiteScoring.HIGHER_PURPLE || modelSiteScoring === PanelSiteScoring.LOWER_PURPLE)
        series.push(OpraRag.Purple);

    const siteSeries: IOpraChartSeries[] = series.map(
        (rag: OpraRag): IOpraChartSeries => ({
            name: HC_THRESHOLD_NAMES[rag] || PURPLE_NAMES[modelSiteScoring],
            type: OpraChartSeriesType.Scatter,
            style: {
                color: HC_COLORS_STANDARD[rag],
                marker: {
                    shape: OpraMarkerShape.Circle,
                    isHollow: false,
                },
                lineStyle: OpraChartLineStyle.Solid,
            },
            hasConfidences: false,
            data:
                panelState.panelData.panelData[0].points
                    ?.filter((p: any) => p.color === rag)
                    ?.map(
                        (p: any): IOpraChartPlot => ({
                            x: p.x,
                            y: p.y,
                            rag,
                            direction: p.direction,
                            score: +p.score.toFixed(2),
                            site: sites[p.siteId]?.Fields['Site.name'],
                            pValue: p.pValue,
                            selected: p.siteId === siteId,
                            hideTooltip: !siteId ? undefined : p.siteId !== siteId,
                        }),
                    ) ?? [],
        }),
    );

    return (
        <ScatterLineChart
            getRef={getRef}
            plotTitle={panelState.chartConfig.plotTitle}
            xAxis={{ title: panelState.chartConfig.xTitle, isDateRange: false }}
            yAxisTitle={panelState.chartConfig.yTitle}
            series={siteSeries}
            showLegend={panelState.hideLegend ? !panelState.hideLegend : true}
            isCustomLegendWidth={false}
            tooltipShowSeriesLabel={tooltipShowSeriesLabel}
            tooltipShowSeriesThreshold={tooltipShowSeriesThreshold}
            hidePlotTitle={panelState.hidePlotTitle}
        />
    );
};

const getRateGroupTrend = ({
    getRef,
    panelState,
    sites,
    siteId,
    study,
    previewMode,
    setLoadingComplete,
    dashboardType,
}: any) => {
    if (!panelState?.panelData?.panelData?.length) {
        if (setLoadingComplete) setLoadingComplete();

        return <span>No data</span>;
    }

    const tooltipShowSeriesLabel = true;
    const tooltipShowSeriesThreshold = false;
    const currentSite =
        !siteId && previewMode
            ? sites[Object.keys(panelState.panelData.panelData[0].rateGroupTrendSites)[0]]
            : siteId
            ? sites[siteId]
            : null;

    const seriesData: IOpraChartSeries[] = [
        {
            name: `${study?.studyNumber ?? 'Study'} Trend`,
            hasConfidences: true,
            type: OpraChartSeriesType.SmoothLine,
            style: {
                color: HC_COLORS_STANDARD[OpraColours.Yellow],
            },
            data:
                panelState.panelData.panelData[0].rateGroupTrendLineStudy?.map(
                    (point: any): IOpraChartPlot => ({
                        x: Number(new Date(point.x)),
                        y: point.y,
                        confidence: {
                            yMin: panelState.panelData.panelData[0].rateGroupTrendLowerThresholdStudy.filter(
                                (lwr: any) => lwr.x === point.x,
                            )[0]?.y,
                            yMax: panelState.panelData.panelData[0].rateGroupTrendUpperThresholdStudy.filter(
                                (lwr: any) => lwr.x === point.x,
                            )[0]?.y,
                        },
                    }),
                ) ?? [],
        },
        {
            name: study?.studyNumber ?? 'Study',
            hasConfidences: false,
            type: OpraChartSeriesType.Line,
            style: {
                color: HC_COLORS_STANDARD[OpraColours.LightBlue],
                marker: {
                    shape: OpraMarkerShape.Circle,
                    isHollow: false,
                },
            },
            data:
                panelState.panelData.panelData[0].rateGroupTrendStudyData?.map((p: any) => ({
                    x: Number(new Date(p.x)),
                    y: p.y,
                })) ?? [],
        },
    ];

    if (currentSite && dashboardType !== DashboardType.STUDY) {
        const sitePlots = panelState.panelData.panelData[0].rateGroupTrendSites[currentSite.Fields['Site.id']];
        if (sitePlots)
            seriesData.push(
                {
                    name: `${currentSite.Fields['Site.name']} Trend`,
                    style: {
                        color: HC_COLORS_STANDARD[OpraColours.Purple],
                    },
                    type: OpraChartSeriesType.SmoothLine,
                    hasConfidences: true,
                    data: sitePlots.line.map((point: any) => ({
                        x: Number(new Date(point.x)),
                        y: point.y,
                        confidence: {
                            yMin: sitePlots.lowerThreshold.filter((lwr: any) => lwr.x === point.x)[0]?.y,
                            yMax: sitePlots.upperThreshold.filter((upr: any) => upr.x === point.x)[0]?.y,
                        },
                    })),
                },
                {
                    name: currentSite.Fields['Site.name'],
                    style: {
                        color: HC_COLORS_STANDARD[OpraColours.DarkBlue],
                        marker: {
                            shape: OpraMarkerShape.Circle,
                            isHollow: false,
                        },
                    },
                    type: OpraChartSeriesType.Line,
                    hasConfidences: false,
                    data: sitePlots.data.map((point: any) => ({
                        x: Number(new Date(point.x)),
                        y: point.y,
                    })),
                },
            );
    }

    return (
        <ScatterLineChart
            hidePlotTitle={panelState.hidePlotTitle}
            getRef={getRef}
            plotTitle={panelState.chartConfig.plotTitle}
            xAxis={{ title: panelState.chartConfig.xTitle, isDateRange: true }}
            yAxisTitle={panelState.chartConfig.yTitle}
            series={seriesData}
            showLegend={panelState.hideLegend ? !panelState.hideLegend : true}
            isCustomLegendWidth={false}
            tooltipShowSeriesLabel={tooltipShowSeriesLabel}
            tooltipShowSeriesThreshold={tooltipShowSeriesThreshold}
        />
    );
};

export { getRateGroup, getRateGroupTrend };
